import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dropdown } from "primereact/dropdown";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getLanguageOptions } from "../../../../helpers/languageHelper";
import * as translateService from "../../../../localization/translateService";
import { getQuestions } from "../constants/smartMatchingQuestions";
import {
  DEFAULT_TRANSLATIONS,
  getTranslationKeysArray,
} from "../constants/smartMatchingQuestionTranslations";
import QuestionSelector from "./QuestionSelector";
import * as smartMatchingService from "../../../../services/smartMatchingService";

const QUESTION_TYPES = {
  SINGLE: "single",
  MULTIPLE: "multiple",
  DROPDOWN: "dropdown",
};

const VIEW_STATES = {
  INTRODUCE: "introduce",
  QUESTION: "question",
  LOADING: "loading",
  COMPLETED: "completed",
  ERROR: "error",
};

export default function SmartMatchingQuestion({ props, onViewResult, onSkipRequest }) {
  const [currentView, setCurrentView] = useState(VIEW_STATES.INTRODUCE);

  const [translations, setTranslations] = useState(DEFAULT_TRANSLATIONS);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [dropdownOptions, setDropdownOptions] = useState({});

  const [questionAnimation, setQuestionAnimation] = useState("animate__fadeIn");
  const [pendingIndex, setPendingIndex] = useState(null);

  const questions = useMemo(() => {
    return getQuestions(translations, props.answerImages || {});
  }, [translations, props]);

  const loadTranslations = useCallback(async () => {
    try {
      const results = await translateService.translateAll(
        getTranslationKeysArray()
      );
      const newTranslations = {};
      Object.keys(DEFAULT_TRANSLATIONS).forEach((k, i) => {
        newTranslations[k] = results[i];
      });
      setTranslations(newTranslations);
    } catch (error) {
      setTranslations(DEFAULT_TRANSLATIONS);
    }
  }, []);

  const getDropdownOptions = useCallback(async (optionKey) => {
    switch (optionKey) {
      case "language":
        const options = await getLanguageOptions();
        return options.map((option) => ({
          name: option.name,
          code: option.code,
          flag: option.flag,
        }));
      default:
        return [];
    }
  }, []);

  const handleViewChange = useCallback((view) => {
    setCurrentView(view);
  }, []);

  const handleViewResult = useCallback(() => {
    onViewResult();
  }, [onViewResult]);

  const proceedToNextQuestion = useCallback(async () => {
    if (currentQuestionIndex < questions.length - 1) {
      setQuestionAnimation("animate__fadeOutLeft");
      setPendingIndex(currentQuestionIndex + 1);
    } else {
      setCurrentView(VIEW_STATES.LOADING);

      try {
        const filteredAnswers = Object.fromEntries(
          Object.entries(selectedAnswers).filter(([_, value]) => {
            if (!Array.isArray(value) || value.length === 0) return false;
            return value[0] != null;
          })
        );
        await smartMatchingService.storedSmartMatching(filteredAnswers);
        setTimeout(() => {
          setCurrentView(VIEW_STATES.COMPLETED);
        }, 2000);
      } catch (error) {
        setCurrentView(VIEW_STATES.ERROR);
      }
    }
  }, [currentQuestionIndex, questions.length, selectedAnswers]);

  const handleNextQuestion = useCallback(() => {
    const currentQuestion = questions[currentQuestionIndex];
    const answer = selectedAnswers[currentQuestion.id];
    
    let hasSelection = false;
    if (currentQuestion.type === QUESTION_TYPES.DROPDOWN) {
      hasSelection = Array.isArray(answer) && answer.length > 0 && answer[0] != null;
    } else {
      hasSelection = Array.isArray(answer) && answer.length > 0;
    }

    if (!hasSelection && onSkipRequest) {
      onSkipRequest(() => proceedToNextQuestion());
    } else {
      proceedToNextQuestion();
    }
  }, [currentQuestionIndex, questions, selectedAnswers, proceedToNextQuestion, onSkipRequest]);

  const handleBackQuestion = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setQuestionAnimation("animate__fadeOutRight");
      setPendingIndex(currentQuestionIndex - 1);
    } else {
      setCurrentView(VIEW_STATES.INTRODUCE);
    }
  }, [currentQuestionIndex]);

  const handleSelectionChange = useCallback(
    (selectedIds) => {
      setSelectedAnswers((prev) => ({
        ...prev,
        [questions[currentQuestionIndex].id]: selectedIds,
      }));
    },
    [currentQuestionIndex, questions]
  );

  const handleQuestionAnimationEnd = useCallback(() => {
    if (pendingIndex !== null) {
      setCurrentQuestionIndex(pendingIndex);
      setQuestionAnimation("animate__fadeIn");
      setPendingIndex(null);
    }
  }, [pendingIndex]);

  const handleRetryStoredSmartMatching = useCallback(async () => {
    setCurrentView(VIEW_STATES.LOADING);
    try {
      const filteredAnswers = Object.fromEntries(
        Object.entries(selectedAnswers).filter(([_, value]) => {
          if (!Array.isArray(value) || value.length === 0) return false;
          return value[0] != null;
        })
      );
      await smartMatchingService.storedSmartMatching(filteredAnswers);
      setCurrentView(VIEW_STATES.COMPLETED);
    } catch (error) {
      setCurrentView(VIEW_STATES.ERROR);
    }
  }, [selectedAnswers]);

  useEffect(() => {
    loadTranslations();
  }, [loadTranslations]);

  useEffect(() => {
    const loadOptions = async () => {
      const currentQuestion = questions[currentQuestionIndex];
      if (
        currentQuestion?.type === QUESTION_TYPES.DROPDOWN &&
        currentQuestion?.optionKey
      ) {
        const options = await getDropdownOptions(currentQuestion.optionKey);
        setDropdownOptions((prev) => ({
          ...prev,
          [currentQuestion.optionKey]: options,
        }));
      }
    };
    loadOptions();
  }, [currentQuestionIndex, questions, getDropdownOptions]);

  useEffect(() => {
  }, []);

  const languageTemplate = useCallback(
    (option) => (
      <div className="justify-content-start align-items-center flex gap-2">
        <div className="keduall-icon flag-circle-country small-size justify-content-center align-items-center flex">
          <span className={`flag img flex ${option.flag}`} />
        </div>
        <div className="flex keduall-typography text-16-normal-400 text-default-txt">
          {option.name}
        </div>
      </div>
    ),
    []
  );

  const imagesCacheRef = useRef({});

  const preloadImages = (urls) => {
    urls.forEach(url => {
      if (!imagesCacheRef.current[url] && url) {
        const img = new Image();
        img.src = url;
        imagesCacheRef.current[url] = img;
      }
    });
  };

  useEffect(() => {
    if (Object.keys(imagesCacheRef.current).length === 0) {
      const allImageUrls = questions?.flatMap(q =>
        q?.answers?.map(a => a?.img)
      );
      preloadImages(allImageUrls);
    }
  }, [questions]);

  const selectedLanguageTemplate = useCallback((option, props) => {
    if (option) {
      return (
        <div className="justify-content-start align-items-center flex gap-2">
          <div className="keduall-icon flag-circle-country small-size justify-content-start align-items-center flex">
            <span className={`flag img flex ${option.flag}`} />
          </div>
          <span className="flex keduall-typography text-16-normal-400 text-default-txt">
            {option.name}
          </span>
        </div>
      );
    }
    return <span className="p-placeholder">{props.placeholder}</span>;
  }, []);

  const templateMap = useMemo(
    () => ({
      languageTemplate,
      selectedLanguageTemplate,
    }),
    [languageTemplate, selectedLanguageTemplate]
  );

  const renderQuestionByType = useCallback(() => {
    const currentQuestion = questions[currentQuestionIndex];

    switch (currentQuestion.type) {
      case QUESTION_TYPES.SINGLE:
      case QUESTION_TYPES.MULTIPLE:
        return (
          <div className="smart-matching-selector flex flex-column justify-content-center align-items-center">
            <QuestionSelector
              imagesCache={imagesCacheRef.current}
              questions={currentQuestion.answers || []}
              mode={currentQuestion.type}
              onSelectionChange={handleSelectionChange}
              initialSelectedIds={selectedAnswers[currentQuestion.id] || []}
              valueKey="value"
            />
          </div>
        );
      case QUESTION_TYPES.DROPDOWN:
        return (
          <div className="smart-matching-dropdown flex flex-column justify-content-center align-items-center">
            <div className="w-full text-left mb-1 keduall-typography text-16-normal-400 text-default-txt">
              {currentQuestion.label}
            </div>
            <Dropdown
              value={selectedAnswers[currentQuestion.id]?.[0] || null}
              options={dropdownOptions[currentQuestion.optionKey] || []}
              onChange={(e) => handleSelectionChange([e.value])}
              placeholder={currentQuestion.placeholder}
              optionLabel="name"
              filter={true}
              optionValue="code"
              className={`w-full text-default-txt input-default-default keduall-typography text-16-normal-400`}
              panelClassName="keduall-form w-20rem dropdown-input-panel"
              valueTemplate={templateMap[currentQuestion.valueTemplateKey]}
              itemTemplate={templateMap[currentQuestion.itemTemplateKey]}
            />
          </div>
        );
      default:
        return null;
    }
  }, [
    currentQuestionIndex,
    questions,
    selectedAnswers,
    dropdownOptions,
    handleSelectionChange,
    templateMap,
  ]);

  const renderIntroductionView = useCallback(
    () => (
      <div className="introduction-view-wrapper flex flex-column align-items-center justify-content-center gap-3 animate__animated animate__fadeIn">
        <div className="introduction-view">
          <div className="introduction-view-content animate__animated animate__fadeInUp">
            <div className="px-2 keduall-typography heading-h4-semibold-600 text-default-txt">
              {translations.introductionTitle}
            </div>
            <div className="keduall-typography text-16-normal-400 text-default-txt">
              {translations.introductionDescription1}
            </div>
            <div className="keduall-typography text-16-normal-400 text-default-txt">
              {translations.introductionDescription2}
            </div>
          </div>
          <img
            className=""
            src={props.smartMatchingImageOverview}
            alt="Smart matching overview"
          />
        </div>
        <div className="p-0 flex justify-content-center align-items-center align-self-stretch">
          <Button
            onClick={() => handleViewChange(VIEW_STATES.QUESTION)}
            className="keduall-buttons button-filled-default-normal-primary"
          >
            {translations.startSmartMatchingButton}
          </Button>
        </div>
      </div>
    ),
    [translations, handleViewChange, props.smartMatchingImageOverview]
  );

  const renderQuestionContent = useCallback(() => {
    const currentQuestion = questions[currentQuestionIndex];

    return (
      <div className="question-view-wrapper w-full flex flex-column gap-3 align-items-center">
        <div
          className={`question-content-animate animate__animated ${questionAnimation}`}
          style={{ "--animate-duration": "0.35s" }}
          onAnimationEnd={handleQuestionAnimationEnd}
        >
          <div className="w-full question-group flex flex-column gap-3">
            <div className="px-2 keduall-typography heading-h4-semibold-600 text-default-txt text-center animate__animated animate__fadeInDown">
              {`${currentQuestionIndex + 1}. ${currentQuestion.title}`}
            </div>
            {currentQuestion.description && (
              <div className="keduall-typography heading-h6-normal-400 text-danger-txt text-center animate__animated animate__fadeIn">
                {currentQuestion.description}
              </div>
            )}
            {renderQuestionByType()}
          </div>
        </div>
        <div className="navigation w-full flex flex-wrap justify-content-between">
          <Button
            onClick={handleBackQuestion}
            className="keduall-buttons button-outlined-default-normal-secondary animate__animated animate__fadeInLeft"
          >
            {translations.backButton}
          </Button>

          <Button
            onClick={handleNextQuestion}
            className="keduall-buttons button-filled-default-normal-primary animate__animated animate__fadeInRight"
            // disabled={!selectedAnswers[currentQuestion.id]?.length}
          >
            {translations.nextButton}
          </Button>
        </div>
      </div>
    );
  }, [
    currentQuestionIndex,
    questions,
    translations,
    // selectedAnswers,
    handleBackQuestion,
    handleNextQuestion,
    renderQuestionByType,
    questionAnimation,
    handleQuestionAnimationEnd,
  ]);

  const renderLoadingView = useCallback(
    () => (
      <div className="smart-matching-loading-wrapper flex flex-column align-items-center justify-content-center gap-3 animate__animated animate__fadeIn">
        <div className="px-2 keduall-typography heading-h4-semibold-600 text-default-txt animate__animated animate__fadeInDown">
          {translations.loadingTitle}
        </div>
        <div className="smart-matching-loading-wrapper-content flex flex-column align-items-center justify-content-center gap-3">
          <div className="flex justify-content-center align-items-center ">
            <ProgressSpinner
              className="spinner-loading"
              strokeWidth="8"
              fill="none"
              animationDuration=".8s"
              color="var(--primary-default)"
            />
          </div>
          <div className="loading-group animate__animated animate__fadeInUp">
            <div className="keduall-typography text-16-normal-400 text-center text-default-txt">
              {translations.loadingDescription}
            </div>
          </div>
        </div>
      </div>
    ),
    [translations]
  );

  const renderCompletedView = useCallback(
    () => (
      <div className="smart-matching-completed-wrapper flex flex-column align-items-center justify-content-center gap-3 animate__animated animate__fadeIn">
        <div className=" text-center flex flex-wrap align-items-center justify-content-center gap-1 animate__animated animate__bounceIn">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="25"
            height="24"
            viewBox="0 0 25 24"
            fill="none"
          >
            <path
              d="M10.5016 16.3336C10.2443 16.314 10.005 16.1943 9.83494 16.0003L5.83494 12.0003C5.74582 11.8137 5.71674 11.6041 5.75171 11.4002C5.78668 11.1964 5.88397 11.0085 6.0302 10.8622C6.17642 10.716 6.36439 10.6187 6.5682 10.5837C6.77202 10.5488 6.98166 10.5778 7.16827 10.667L10.4616 13.9603L21.8349 2.66697C22.0215 2.57785 22.2312 2.54877 22.435 2.58374C22.6388 2.61871 22.8268 2.716 22.973 2.86223C23.1192 3.00846 23.2165 3.19642 23.2515 3.40023C23.2865 3.60405 23.2574 3.8137 23.1683 4.0003L11.1683 16.0003C10.9982 16.1943 10.7589 16.314 10.5016 16.3336Z"
              fill="#22C55E"
            />
            <path
              d="M12.5001 24.0001C10.3554 23.9967 8.25084 23.4185 6.40537 22.3258C4.55991 21.233 3.04104 19.6657 2.00681 17.7868C1.22066 16.3868 0.734779 14.8383 0.580144 13.2401C0.336118 10.8962 0.787624 8.53236 1.87834 6.4434C2.96905 4.35445 4.65069 2.63288 6.71348 1.49345C8.11351 0.707294 9.66196 0.221417 11.2601 0.066782C12.8523 -0.104487 14.4627 0.049748 15.9935 0.520116C16.1301 0.547411 16.2595 0.602948 16.3734 0.683169C16.4873 0.763389 16.5832 0.866522 16.6549 0.985957C16.7266 1.10539 16.7726 1.23849 16.7898 1.37673C16.8071 1.51496 16.7953 1.65529 16.7552 1.7887C16.7151 1.92211 16.6475 2.04566 16.5569 2.15144C16.4662 2.25723 16.3545 2.3429 16.2288 2.40298C16.1031 2.46305 15.9662 2.4962 15.827 2.5003C15.6877 2.5044 15.5492 2.47936 15.4201 2.42678C14.125 2.03734 12.765 1.91042 11.4201 2.05345C10.0908 2.18957 8.80275 2.59322 7.63348 3.24012C6.50699 3.86159 5.511 4.69459 4.70014 5.69345C3.86529 6.711 3.24187 7.8849 2.86639 9.1464C2.49092 10.4079 2.37094 11.7317 2.51348 13.0401C2.6496 14.3695 3.05325 15.6575 3.70014 16.8268C4.32161 17.9533 5.15462 18.9493 6.15348 19.7601C7.17102 20.595 8.34493 21.2184 9.60643 21.5939C10.8679 21.9693 12.1917 22.0893 13.5001 21.9468C14.8295 21.8107 16.1175 21.407 17.2868 20.7601C18.4133 20.1386 19.4093 19.3056 20.2201 18.3068C21.055 17.2892 21.6784 16.1153 22.0539 14.8538C22.4294 13.5923 22.5494 12.2686 22.4068 10.9601C22.3937 10.8227 22.4077 10.684 22.4482 10.552C22.4887 10.4199 22.5548 10.2972 22.6427 10.1907C22.7306 10.0842 22.8386 9.99612 22.9605 9.93139C23.0825 9.86666 23.216 9.82658 23.3535 9.81345C23.4909 9.80032 23.6296 9.81439 23.7616 9.85485C23.8936 9.89532 24.0164 9.96139 24.1229 10.0493C24.2294 10.1372 24.3175 10.2452 24.3822 10.3672C24.4469 10.4891 24.487 10.6227 24.5001 10.7601C24.7429 13.1054 24.2894 15.4703 23.1963 17.5594C22.1031 19.6485 20.4187 21.3692 18.3535 22.5068C16.9386 23.3251 15.3662 23.8341 13.7401 24.0001C13.3268 24.0001 12.9001 24.0001 12.5001 24.0001Z"
              fill="#22C55E"
            />
          </svg>
          <div className="px-2 keduall-typography heading-h4-semibold-600 text-default-txt">
            {translations.loadingCompletedTitle}
          </div>
        </div>
        <div className="completed-description animate__animated animate__fadeInUp">
          <div className="keduall-typography text-16-normal-400 text-center text-default-txt">
            {translations.loadingCompletedDescription}
          </div>
        </div>
        <div className="p-0 flex justify-content-center align-items-center align-self-stretch">
          <Button
            onClick={() => handleViewResult()}
            className="keduall-buttons button-filled-default-normal-primary "
          >
            {translations.viewResultButton}
          </Button>
        </div>
      </div>
    ),
    [translations, handleViewResult]
  );

  const renderErrorView = useCallback(
    () => (
      <div className="smart-matching-error-wrapper flex flex-column align-items-center justify-content-center gap-3 animate__animated animate__fadeIn">
        <div className=" text-center flex flex-wrap align-items-center justify-content-center gap-1 animate__animated animate__bounceIn">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 21C10.22 21 8.47991 20.4722 6.99987 19.4832C5.51983 18.4943 4.36628 17.0887 3.68509 15.4442C3.0039 13.7996 2.82567 11.99 3.17294 10.2442C3.5202 8.49836 4.37737 6.89472 5.63604 5.63604C6.89472 4.37737 8.49836 3.5202 10.2442 3.17294C11.99 2.82567 13.7996 3.0039 15.4442 3.68509C17.0887 4.36628 18.4943 5.51983 19.4832 6.99987C20.4722 8.47991 21 10.22 21 12C21 14.387 20.0518 16.6761 18.364 18.364C16.6761 20.0518 14.387 21 12 21ZM12 4.5C10.5166 4.5 9.0666 4.93987 7.83323 5.76398C6.59986 6.58809 5.63856 7.75943 5.07091 9.12988C4.50325 10.5003 4.35473 12.0083 4.64411 13.4632C4.9335 14.918 5.64781 16.2544 6.6967 17.3033C7.7456 18.3522 9.08197 19.0665 10.5368 19.3559C11.9917 19.6453 13.4997 19.4968 14.8701 18.9291C16.2406 18.3614 17.4119 17.4001 18.236 16.1668C19.0601 14.9334 19.5 13.4834 19.5 12C19.5 10.0109 18.7098 8.10323 17.3033 6.6967C15.8968 5.29018 13.9891 4.5 12 4.5Z"
              fill="#E80000"
            />
            <path
              d="M12 13C11.8019 12.9974 11.6126 12.9176 11.4725 12.7775C11.3324 12.6374 11.2526 12.4481 11.25 12.25V8.75C11.25 8.55109 11.329 8.36032 11.4697 8.21967C11.6103 8.07902 11.8011 8 12 8C12.1989 8 12.3897 8.07902 12.5303 8.21967C12.671 8.36032 12.75 8.55109 12.75 8.75V12.25C12.7474 12.4481 12.6676 12.6374 12.5275 12.7775C12.3874 12.9176 12.1981 12.9974 12 13Z"
              fill="#E80000"
            />
            <path
              d="M12 16C11.8019 15.9974 11.6126 15.9176 11.4725 15.7775C11.3324 15.6374 11.2526 15.4481 11.25 15.25V14.75C11.25 14.5511 11.329 14.3603 11.4697 14.2197C11.6103 14.079 11.8011 14 12 14C12.1989 14 12.3897 14.079 12.5303 14.2197C12.671 14.3603 12.75 14.5511 12.75 14.75V15.25C12.7474 15.4481 12.6676 15.6374 12.5275 15.7775C12.3874 15.9176 12.1981 15.9974 12 16Z"
              fill="#E80000"
            />
          </svg>

          <div className="px-2 keduall-typography heading-h4-semibold-600 text-default-txt">
            {translations.loadingErrorTitle}
          </div>
        </div>
        <div className="error-description animate__animated animate__fadeInUp">
          <div className="keduall-typography text-16-normal-400 text-center text-default-txt">
            {translations.loadingErrorDescription}
          </div>
        </div>
        <div className="p-0 flex justify-content-center align-items-center align-self-stretch">
          <Button
            onClick={handleRetryStoredSmartMatching}
            className="keduall-buttons button-filled-default-normal-primary "
          >
            {translations.retryButton}
          </Button>
        </div>
      </div>
    ),
    [translations, handleRetryStoredSmartMatching]
  );

  return (
    <div className="smart-matching-question-wrapper">
      {currentView === VIEW_STATES.INTRODUCE && renderIntroductionView()}
      {currentView === VIEW_STATES.QUESTION && renderQuestionContent()}
      {currentView === VIEW_STATES.LOADING && renderLoadingView()}
      {currentView === VIEW_STATES.COMPLETED && renderCompletedView()}
      {currentView === VIEW_STATES.ERROR && renderErrorView()}
    </div>
  );
}
